import { translate } from '../../../helpers/translations/translator'
import { AbsenceHours, WeekWithHours } from '../../../models'
import { Absence, AbsenceFormData } from '../../entities/Absence/Absence'
import { deleteAbsenceApiCall, patchAbsenceApiCall, postAbsenceApiCall } from '../../entities/Absence/AbsenceService'
import { updateAbsenceHours } from '../../entities/AbsenceHour/AbsenceHourHelper'
import { postAbsenceHoursApiCall } from '../../entities/AbsenceHour/AbsenceHourService'
import { isFetchResultSuccessful } from '../../entities/FetchResult'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '../../entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '../../entities/NotificationCenter/NotificationCenterService'
import { ToastType } from '../../types'
import { TypedDispatch } from '../store'
import { setToast } from '../toasts/toastsReducer'
import { setError, setIsLoading } from './absencesReducer'

export const addAbsence = (
    userId: string,
    data: AbsenceFormData,
    weeksWithHours: WeekWithHours[],
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.ABSENCE,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await postAbsenceApiCall(data, userId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        await Promise.all([
            ...weeksWithHours.map(
                week => postAbsenceHoursApiCall(
                    response.data.id,
                    responseTracker.data,
                    week.payrollPeriod ? week.payrollPeriod.id : undefined,
                    week.hours,
                    week.weekNumber,
                    data.waitingDayHours,
                ),
            ),
        ])

        dispatch(setToast({
            text: translate('pages.absences.addAbsenceSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[addAbsence]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const patchAbsence = (
    absence: Absence,
    data: AbsenceFormData,
    absenceHours: AbsenceHours[],
    weeksWithHours: WeekWithHours[],
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.ABSENCE,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        dispatch(setIsLoading(false))
        return
    }

    try {
        const absenceResponse = await patchAbsenceApiCall(
            absence.id,
            data,
            responseTracker.data,
        )

        if (! isFetchResultSuccessful(absenceResponse)) {
            dispatch(setError(absenceResponse.error))
            return
        }

        const hourOperations = updateAbsenceHours(
            absence.id,
            absenceHours,
            responseTracker.data,
            weeksWithHours,
            data.waitingDayHours,
        )

        await hourOperations.reduce(async (prev, operation) => {
            await prev
            try {
                const response = await operation()
                if (! isFetchResultSuccessful(response)) {
                    dispatch(setError(response.error))
                }
            } catch (err) {
                console.error('[Hour update failed]', err)
            }
        }, Promise.resolve())

        dispatch(setToast({
            text: translate('pages.absences.editAbsenceSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[patchAbsence]', error)
        dispatch(setError(error instanceof Error ? error.message : String(error)))
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const deleteAbsence = (absence: Absence) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.ABSENCE,
        NotificationCenterAction.DELETED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await deleteAbsenceApiCall(absence.id, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
        }

        dispatch(setToast({
            text: translate('pages.absences.deleteAbsenceSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[deleteLeaveOfAbsence]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}
